import { ReactNode } from 'react';
import { Avatar } from 'antd';
import { AreaChart, LineChart, Radar, ScatterChart, SquareMousePointer } from 'lucide-react';
import {
    TbAlignJustified,
    TbAnalyze,
    TbChartArcs3,
    TbChartDonut4,
    TbChartInfographic,
    TbChartPie,
    TbChartSankey,
    TbDashboard,
    TbGrid4X4,
    TbMap,
    TbNumbers,
    TbSeparator,
    TbSourceCode,
    TbTable,
    TbTableOptions,
    TbTypography,
    TbWorld,
} from 'react-icons/tb';

import {
    BoxSVGIcon,
    ConfluenceSVGIcon,
    DropboxSVGIcon,
    GitHubSVGIcon,
    GoogleCalendarSVGIcon,
    GoogleDocsSVGIcon,
    GoogleDriveSVGIcon,
    GoogleSheetsSVGIcon,
    GraphySVGIcon,
    HexSVGIcon,
    LookerSVGIcon,
    LoomSVGIcon,
    LucidSVGIcon,
    MicrosoftOneDriveSVGIcon,
    MicrosoftOutlookSVGIcon,
    MicrosoftPowerBISVGIcon,
    MiroSVGIcon,
    NotionSVGIcon,
    TableauSVGIcon,
    VimeoSVGIcon,
    YouTubeSVGIcon,
    ZohoAnalyticsSVGIcon,
} from '@/assets';
import { isReportComponentAvailable } from '@/shared/constants';
import { imgLogoStyling } from '@/theme';
import { ReportComponentType } from '@/types';

interface ReportComponentOptionsToken {
    colorBorder: string;
    colorTextTertiary: string;
}

export interface ReportComponentOption {
    defaultTileHeight?: number;
    defaultTileWidth?: number;
    description: ReactNode;
    icon: ReactNode;
    key: ReportComponentType;
    label: string;
}

export interface ReportComponentOptionSection {
    icon: ReactNode;
    key: string;
    label: string;
    options: ReportComponentOption[];
}

export const createReportComponentOptions = (token: ReportComponentOptionsToken): ReportComponentOptionSection[] =>
    [
        {
            key: 'content',
            label: 'Content',
            icon: <SquareMousePointer size='0.75rem' style={{ verticalAlign: '-0.125rem', marginRight: '0.25rem' }} />,
            options: [
                {
                    key: ReportComponentType.TITLE,
                    icon: <TbTypography size='1rem' style={{ verticalAlign: '-0.2125rem' }} />,
                    label: 'Title',
                    description: <small style={{ color: token.colorTextTertiary }}>Display a title.</small>,
                },
                {
                    key: ReportComponentType.DESCRIPTION,
                    icon: <TbAlignJustified size='1rem' style={{ verticalAlign: '-0.2125rem' }} />,
                    label: 'Description',
                    description: <small style={{ color: token.colorTextTertiary }}>Display a description.</small>,
                },
                {
                    key: ReportComponentType.DIVIDER,
                    icon: <TbSeparator size='1rem' style={{ verticalAlign: '-0.2125rem' }} />,
                    label: 'Divider',
                    description: <small style={{ color: token.colorTextTertiary }}>Display a divider.</small>,
                },
            ],
        },
        {
            key: 'dataVisuals',
            label: 'Data Visualizations',
            icon: <TbAnalyze style={{ verticalAlign: '-0.125rem', marginRight: '0.25rem' }} />,
            options: [
                {
                    key: ReportComponentType.COUNTERCYCLICAL_VANTAGE,
                    icon: <TbSourceCode size='1rem' style={{ verticalAlign: '-0.2125rem' }} />,
                    label: 'Vantage',
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>
                            Query your governed read model with SQL.
                        </small>
                    ),
                    defaultTileHeight: 12,
                    defaultTileWidth: 6,
                },
                {
                    key: ReportComponentType.STATISTIC,
                    icon: <TbNumbers size='1rem' style={{ verticalAlign: '-0.2125rem' }} />,
                    label: 'Key Statistic',
                    description: <small style={{ color: token.colorTextTertiary }}>Display an important value.</small>,
                },
                {
                    key: ReportComponentType.MULTIPLE_STATISTICS,
                    icon: <TbTableOptions size='1rem' />,
                    label: 'Statistic Group',
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>
                            Display a compact group of important values.
                        </small>
                    ),
                },
                {
                    key: ReportComponentType.TABLE,
                    icon: <TbTable size='1rem' />,
                    label: 'Table',
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>
                            Display query results in a structured table.
                        </small>
                    ),
                },
                {
                    key: ReportComponentType.MAP,
                    icon: <TbMap size='1rem' />,
                    label: 'Map',
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>
                            Plot geographic query results on an interactive map.
                        </small>
                    ),
                },
                {
                    key: ReportComponentType.GLOBE,
                    icon: <TbWorld size='1rem' />,
                    label: 'Globe',
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>
                            Display a saved Global Trade view on an interactive globe.
                        </small>
                    ),
                    defaultTileHeight: 16,
                    defaultTileWidth: 6,
                },
            ],
        },
        {
            key: 'charts',
            label: 'Charts',
            icon: <TbChartInfographic style={{ verticalAlign: '-0.125rem', marginRight: '0.25rem' }} />,
            options: [
                {
                    key: ReportComponentType.FUNNEL_CHART,
                    icon: (
                        <svg
                            style={{ verticalAlign: '-0.2125rem' }}
                            xmlns='http://www.w3.org/2000/svg'
                            width='1rem'
                            height='1rem'
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            className='icon icon-tabler icons-tabler-outline icon-tabler-chart-funnel'
                        >
                            <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                            <path d='M4.387 3h15.226a1 1 0 0 1 .948 1.316l-5.105 15.316a2 2 0 0 1 -1.898 1.368h-3.116a2 2 0 0 1 -1.898 -1.368l-5.104 -15.316a1 1 0 0 1 .947 -1.316' />
                            <path d='M5 9h14' />
                            <path d='M7 15h10' />
                        </svg>
                    ),
                    label: 'Funnel Chart',
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>
                            Display staged values in a funnel visualization.
                        </small>
                    ),
                },
                {
                    key: ReportComponentType.SANKEY_CHART,
                    icon: <TbChartSankey size='1rem' style={{ verticalAlign: '-0.2125rem' }} />,
                    label: 'Sankey Chart',
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>
                            Display source-to-target flows with weighted links.
                        </small>
                    ),
                },
                {
                    key: ReportComponentType.HEAT_MAP,
                    icon: <TbGrid4X4 size='1rem' style={{ verticalAlign: '-0.2125rem' }} />,
                    label: 'Heat Map',
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>
                            Display values across two dimensions in a matrix.
                        </small>
                    ),
                },
                {
                    key: ReportComponentType.SCATTER_PLOT,
                    icon: <ScatterChart size='1rem' style={{ verticalAlign: '-0.2125rem' }} />,
                    label: 'Scatter Plot',
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>
                            Compare two metrics across observations.
                        </small>
                    ),
                },
                {
                    key: ReportComponentType.BAR_CHART,
                    icon: (
                        <svg
                            xmlns='http://www.w3.org/2000/svg'
                            style={{ verticalAlign: '-0.2125rem' }}
                            width='1rem'
                            height='1rem'
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            className='lucide lucide-chart-column-stacked'
                        >
                            <path d='M11 13H7' />
                            <path d='M19 9h-4' />
                            <path d='M3 3v16a2 2 0 0 0 2 2h16' />
                            <rect x='15' y='5' width='4' height='12' rx='1' />
                            <rect x='7' y='8' width='4' height='9' rx='1' />
                        </svg>
                    ),
                    label: 'Bar Chart',
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>
                            Display values in a bar chart visualization.
                        </small>
                    ),
                },
                {
                    key: ReportComponentType.GAUGE_CHART,
                    icon: <TbDashboard size='1rem' style={{ verticalAlign: '-0.2125rem' }} />,
                    label: 'Gauge Chart',
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>
                            Display metric progress in a gauge visualization.
                        </small>
                    ),
                },
                {
                    key: ReportComponentType.DONUT_CHART,
                    icon: <TbChartDonut4 size='1rem' style={{ verticalAlign: '-0.2125rem' }} />,
                    label: 'Donut Chart',
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>
                            Display metric composition in a donut chart.
                        </small>
                    ),
                },
                {
                    key: ReportComponentType.PIE_CHART,
                    icon: <TbChartPie size='1rem' style={{ verticalAlign: '-0.2125rem' }} />,
                    label: 'Pie Chart',
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>
                            Display metric composition in a pie chart.
                        </small>
                    ),
                },
                {
                    key: ReportComponentType.AREA_CHART,
                    icon: <AreaChart size='1rem' style={{ verticalAlign: '-0.2125rem' }} />,
                    label: 'Area Chart',
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>Display trends as filled time series.</small>
                    ),
                },
                {
                    key: ReportComponentType.LINE_CHART,
                    icon: <LineChart size='1rem' style={{ verticalAlign: '-0.2125rem' }} />,
                    label: 'Line Chart',
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>
                            Display trends across a continuous dimension.
                        </small>
                    ),
                },
                {
                    key: ReportComponentType.RADAR_CHART,
                    icon: <Radar size='1rem' style={{ verticalAlign: '-0.2125rem' }} />,
                    label: 'Radar Chart',
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>
                            Compare several metrics across a radial axis.
                        </small>
                    ),
                },
                {
                    key: ReportComponentType.RADIAL_CHART,
                    icon: <TbChartArcs3 size='1rem' style={{ verticalAlign: '-0.2125rem' }} />,
                    label: 'Radial Chart',
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>
                            Display grouped metric values as radial bars.
                        </small>
                    ),
                },
                {
                    key: ReportComponentType.CHORD_CHART,
                    icon: <TbChartDonut4 size='1rem' style={{ verticalAlign: '-0.2125rem' }} />,
                    label: 'Chord Chart',
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>
                            Display weighted relationships between entities.
                        </small>
                    ),
                },
            ],
        },
        {
            key: 'embeds',
            label: 'Embeds',
            icon: (
                <TbSourceCode
                    size='0.75rem'
                    style={{ verticalAlign: '-0.125rem', marginRight: '0.25rem', transform: 'rotate(-180deg)' }}
                />
            ),
            options: [
                {
                    key: ReportComponentType.EMBED,
                    icon: (
                        <TbSourceCode
                            size='1rem'
                            style={{
                                verticalAlign: '-0.25rem',
                                transform: 'rotate(-180deg)',
                            }}
                        />
                    ),
                    label: 'Embed',
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>
                            Display an embedded iframe in your Report.
                        </small>
                    ),
                },
                {
                    key: ReportComponentType.YOUTUBE,
                    icon: (
                        <Avatar
                            size={20}
                            shape='square'
                            icon={
                                <img
                                    src={YouTubeSVGIcon}
                                    alt='YouTube'
                                    style={{ ...imgLogoStyling, transform: 'scale(0.75)' }}
                                />
                            }
                            style={{ border: `1px solid ${token.colorBorder}` }}
                        />
                    ),
                    label: 'YouTube',
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>
                            Display a YouTube video in your Report.
                        </small>
                    ),
                },
                {
                    key: ReportComponentType.LOOM,
                    icon: (
                        <Avatar
                            size={20}
                            shape='square'
                            icon={
                                <img
                                    src={LoomSVGIcon}
                                    alt='Loom'
                                    style={{ ...imgLogoStyling, transform: 'scale(0.75)' }}
                                />
                            }
                            style={{ border: `1px solid ${token.colorBorder}` }}
                        />
                    ),
                    label: 'Loom',
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>Display a Loom video in your Report.</small>
                    ),
                },
                {
                    key: ReportComponentType.VIMEO,
                    icon: (
                        <Avatar
                            size={20}
                            shape='square'
                            icon={<img src={VimeoSVGIcon} alt='Vimeo' style={{ ...imgLogoStyling }} />}
                            style={{ border: `1px solid ${token.colorBorder}` }}
                        />
                    ),
                    label: 'Vimeo',
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>Display a Vimeo video in your Report.</small>
                    ),
                },
                {
                    key: ReportComponentType.GOOGLE_CALENDAR,
                    icon: (
                        <Avatar
                            size={20}
                            shape='square'
                            icon={
                                <img
                                    src={GoogleCalendarSVGIcon}
                                    alt='Google Calendar'
                                    style={{ ...imgLogoStyling, transform: 'scale(0.75)' }}
                                />
                            }
                            style={{ border: `1px solid ${token.colorBorder}` }}
                        />
                    ),
                    label: 'Google Calendar',
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>
                            Display a Google Calendar directly in your Report.
                        </small>
                    ),
                },
                {
                    key: ReportComponentType.MICROSOFT_OUTLOOK_CALENDAR,
                    icon: (
                        <Avatar
                            size={20}
                            shape='square'
                            icon={
                                <img
                                    src={MicrosoftOutlookSVGIcon}
                                    alt='Microsoft Outlook Calendar'
                                    style={{ ...imgLogoStyling, transform: 'scale(0.75)' }}
                                />
                            }
                            style={{ border: `1px solid ${token.colorBorder}` }}
                        />
                    ),
                    label: 'Microsoft Outlook Calendar',
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>
                            Display a Microsoft Outlook Calendar directly in your Report.
                        </small>
                    ),
                },
                {
                    key: ReportComponentType.GOOGLE_DRIVE,
                    icon: (
                        <Avatar
                            size={20}
                            shape='square'
                            icon={<img src={GoogleDriveSVGIcon} alt='Google Drive' style={{ ...imgLogoStyling }} />}
                            style={{ border: `1px solid ${token.colorBorder}` }}
                        />
                    ),
                    label: 'Google Drive',
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>
                            Display a Google Drive file directly in your Report.
                        </small>
                    ),
                },
                {
                    key: ReportComponentType.GOOGLE_DOCS,
                    icon: (
                        <Avatar
                            size={20}
                            shape='square'
                            icon={
                                <img
                                    src={GoogleDocsSVGIcon}
                                    alt='Google Docs'
                                    style={{ ...imgLogoStyling, transform: 'scale(0.75)' }}
                                />
                            }
                            style={{ border: `1px solid ${token.colorBorder}` }}
                        />
                    ),
                    label: 'Google Docs',
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>
                            Display a Google Docs view in your Report.
                        </small>
                    ),
                },
                {
                    key: ReportComponentType.GOOGLE_SHEETS,
                    icon: (
                        <Avatar
                            size={20}
                            shape='square'
                            icon={
                                <img
                                    src={GoogleSheetsSVGIcon}
                                    alt='Google Sheets'
                                    style={{ ...imgLogoStyling, transform: 'scale(0.75)' }}
                                />
                            }
                            style={{ border: `1px solid ${token.colorBorder}` }}
                        />
                    ),
                    label: 'Google Sheets',
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>
                            Display a Google Sheets view in your Report.
                        </small>
                    ),
                },
                {
                    key: ReportComponentType.MICROSOFT_ONEDRIVE,
                    icon: (
                        <Avatar
                            size={20}
                            shape='square'
                            icon={
                                <img
                                    src={MicrosoftOneDriveSVGIcon}
                                    alt='Microsoft OneDrive'
                                    style={{ ...imgLogoStyling }}
                                />
                            }
                            style={{ border: `1px solid ${token.colorBorder}` }}
                        />
                    ),
                    label: 'Microsoft OneDrive',
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>
                            Display a shared OneDrive file directly in your Report.
                        </small>
                    ),
                },
                {
                    key: ReportComponentType.DROPBOX,
                    icon: (
                        <Avatar
                            size={20}
                            shape='square'
                            icon={<img src={DropboxSVGIcon} alt='Dropbox' style={{ ...imgLogoStyling }} />}
                            style={{ border: `1px solid ${token.colorBorder}` }}
                        />
                    ),
                    label: 'Dropbox',
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>
                            Display a shared Dropbox file directly in your Report.
                        </small>
                    ),
                },
                {
                    key: ReportComponentType.BOX,
                    icon: (
                        <Avatar
                            size={20}
                            shape='square'
                            icon={<img src={BoxSVGIcon} alt='Box' style={{ ...imgLogoStyling }} />}
                            style={{ border: `1px solid ${token.colorBorder}` }}
                        />
                    ),
                    label: 'Box',
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>
                            Display a shared Box file directly in your Report.
                        </small>
                    ),
                },
                {
                    key: ReportComponentType.NOTION,
                    icon: (
                        <Avatar
                            size={20}
                            shape='square'
                            icon={
                                <img
                                    src={NotionSVGIcon}
                                    alt='Notion'
                                    style={{ ...imgLogoStyling, transform: 'scale(0.75)' }}
                                />
                            }
                            style={{ border: `1px solid ${token.colorBorder}` }}
                        />
                    ),
                    label: 'Notion',
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>
                            Display Notion pages & databases in your Report.
                        </small>
                    ),
                },
                {
                    key: ReportComponentType.GITHUB,
                    icon: (
                        <Avatar
                            size={20}
                            shape='square'
                            icon={<img src={GitHubSVGIcon} alt='GitHub' style={{ ...imgLogoStyling }} />}
                            style={{ border: `1px solid ${token.colorBorder}` }}
                        />
                    ),
                    label: 'GitHub',
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>Display a GitHub Gist in your Report.</small>
                    ),
                },
                {
                    key: ReportComponentType.HEX,
                    icon: (
                        <Avatar
                            size={20}
                            shape='square'
                            icon={<img src={HexSVGIcon} alt='Hex' style={{ ...imgLogoStyling }} />}
                            style={{ border: `1px solid ${token.colorBorder}` }}
                        />
                    ),
                    label: 'Hex',
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>Display a Hex notebook in your Report.</small>
                    ),
                },
                {
                    key: ReportComponentType.CONFLUENCE,
                    icon: (
                        <Avatar
                            size={20}
                            shape='square'
                            icon={<img src={ConfluenceSVGIcon} alt='Confluence' style={{ ...imgLogoStyling }} />}
                            style={{ border: `1px solid ${token.colorBorder}` }}
                        />
                    ),
                    label: 'Confluence',
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>
                            Display a published Confluence page in your Report.
                        </small>
                    ),
                },
                {
                    key: ReportComponentType.MIRO,
                    icon: (
                        <Avatar
                            size={20}
                            shape='square'
                            icon={
                                <img
                                    src={MiroSVGIcon}
                                    alt='Miro'
                                    style={{ ...imgLogoStyling, transform: 'scale(0.75)' }}
                                />
                            }
                            style={{ border: `1px solid ${token.colorBorder}` }}
                        />
                    ),
                    label: 'Miro',
                    description: <small style={{ color: token.colorTextTertiary }}>Display a Miro board.</small>,
                },
                {
                    key: ReportComponentType.LUCID,
                    icon: (
                        <Avatar
                            size={20}
                            shape='square'
                            icon={<img src={LucidSVGIcon} alt='Lucid' style={{ ...imgLogoStyling }} />}
                            style={{ border: `1px solid ${token.colorBorder}` }}
                        />
                    ),
                    label: 'Lucid',
                    description: <small style={{ color: token.colorTextTertiary }}>Display a Lucid board.</small>,
                },
                {
                    key: ReportComponentType.GRAPHY,
                    icon: (
                        <Avatar
                            size={20}
                            shape='square'
                            icon={<img src={GraphySVGIcon} alt='Graphy' style={{ ...imgLogoStyling }} />}
                            style={{ border: `1px solid ${token.colorBorder}` }}
                        />
                    ),
                    label: 'Graphy',
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>Display a Graphy chart in your Report.</small>
                    ),
                },
                {
                    key: ReportComponentType.MICROSOFT_POWER_BI,
                    label: 'Microsoft Power BI',
                    icon: (
                        <Avatar
                            size={20}
                            shape='square'
                            icon={
                                <img
                                    src={MicrosoftPowerBISVGIcon}
                                    alt='Microsoft Power BI'
                                    style={{ ...imgLogoStyling, transform: 'scale(0.75)' }}
                                />
                            }
                            style={{ border: `1px solid ${token.colorBorder}` }}
                        />
                    ),
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>
                            Display a Microsoft BI view directly in your Report.
                        </small>
                    ),
                },
                {
                    key: ReportComponentType.LOOKER,
                    label: 'Looker',
                    icon: (
                        <Avatar
                            size={20}
                            shape='square'
                            icon={
                                <img
                                    src={LookerSVGIcon}
                                    alt='Looker'
                                    style={{ ...imgLogoStyling, transform: 'scale(0.75)' }}
                                />
                            }
                            style={{ border: `1px solid ${token.colorBorder}` }}
                        />
                    ),
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>
                            Display a Looker view directly in your Report.
                        </small>
                    ),
                },
                {
                    key: ReportComponentType.TABLEAU,
                    label: 'Tableau',
                    icon: (
                        <Avatar
                            size={20}
                            shape='square'
                            icon={
                                <img
                                    src={TableauSVGIcon}
                                    alt='Tableau'
                                    style={{ ...imgLogoStyling, transform: 'scale(0.75)' }}
                                />
                            }
                            style={{ border: `1px solid ${token.colorBorder}` }}
                        />
                    ),
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>
                            Display a Tableau view directly in your Report.
                        </small>
                    ),
                },
                {
                    key: ReportComponentType.ZOHO_ANALYTICS,
                    label: 'Zoho Analytics',
                    icon: (
                        <Avatar
                            size={20}
                            shape='square'
                            icon={
                                <img
                                    src={ZohoAnalyticsSVGIcon}
                                    alt='Zoho Analytics'
                                    style={{ ...imgLogoStyling, transform: 'scale(0.75)' }}
                                />
                            }
                            style={{ border: `1px solid ${token.colorBorder}` }}
                        />
                    ),
                    description: (
                        <small style={{ color: token.colorTextTertiary }}>
                            Display a Zoho Analytics view directly in your Report.
                        </small>
                    ),
                },
            ],
        },
    ].map((group) => ({
        ...group,
        options: group.options.filter((option) => isReportComponentAvailable(option.key)),
    }));
